// Nouveautés - Page publique
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Sparkles, ShoppingCart, Package, ChevronRight, Eye,
  Truck, Shield, Star, Clock, Calendar, Zap, Tag, Award
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

import MainNavigation from '../components/layout/MainNavigation';
import UnifiedHeader from '../components/layout/UnifiedHeader';
import { useBuyer } from '../contexts/BuyerContext';

// Fonction pour calculer "il y a X jours"
function getTimeAgo(dateString) {
  const now = new Date();
  const date = new Date(dateString);
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return "Aujourd'hui";
  if (diffDays === 1) return "Hier";
  if (diffDays < 7) return `Il y a ${diffDays} jours`;
  if (diffDays < 14) return "La semaine dernière";
  return `Il y a ${Math.floor(diffDays / 7)} semaines`;
}

// Carte Produit Nouveauté
function NewProductCard({ product, onAddToCart }) {
  const navigate = useNavigate();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -5 }}
      className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 hover:shadow-xl transition-all relative"
    >
      {/* Badge Nouveau */}
      <div className="absolute top-3 left-3 z-10 bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg">
        <Sparkles size={12} />
        NOUVEAU
      </div>

      {/* Badge date */}
      <div className="absolute top-3 right-3 z-10 bg-white/90 backdrop-blur text-gray-700 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 shadow">
        <Clock size={10} />
        {getTimeAgo(product.createdAt)}
      </div>

      {/* Image produit */}
      <Link to={`/produit/${product.id}`} className="block">
        <div className="relative aspect-square bg-gray-50 cursor-pointer group overflow-hidden">
          {product.image ? (
            <img 
              src={product.image} 
              alt={product.title} 
              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-100">
              <Package size={48} className="text-gray-300" />
            </div>
          )}
          {/* Overlay hover */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
            <span className="opacity-0 group-hover:opacity-100 bg-white/90 text-gray-900 px-4 py-2 rounded-full font-medium text-sm transition-opacity">
              Voir le produit
            </span>
          </div>
        </div>
      </Link>

      {/* Infos produit */}
      <div className="p-4">
        <Link to={`/produit/${product.id}`}>
          <h3 className="font-semibold text-gray-900 line-clamp-2 mb-2 min-h-[44px] hover:text-purple-600 transition-colors">
            {product.title}
          </h3>
        </Link>

        {/* Marque */}
        <p className="text-sm text-gray-500 mb-2">{product.brand}</p>

        {/* Prix */}
        <div className="flex items-baseline gap-2 mb-3">
          <span className="text-2xl font-bold text-gray-900">{product.price}€</span>
          {product.originalPrice && (
            <>
              <span className="text-base text-gray-400 line-through">{product.originalPrice}€</span>
              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                -{Math.round((1 - product.price / product.originalPrice) * 100)}%
              </span>
            </>
          )}
        </div>

        {/* Stock */}
        {product.isLimited && (
          <div className="flex items-center gap-1.5 text-orange-600 text-xs mb-3">
            <Zap size={14} />
            <span className="font-medium">Stock limité - {product.stock} restants</span>
          </div>
        )}

        {/* Livraison */}
        <div className="flex items-center gap-1.5 text-green-600 text-xs mb-4">
          <Truck size={14} />
          <span className="font-medium">Livraison offerte</span>
        </div>

        {/* Boutons */}
        <div className="flex gap-2">
          <Button 
            onClick={() => navigate(`/produit/${product.id}`)}
            variant="outline"
            className="flex-1 border-gray-300 hover:border-blue-500 hover:text-blue-600 font-medium py-2.5 rounded-xl flex items-center justify-center gap-1.5"
          >
            <Eye size={16} />
            Voir
          </Button>
          <Button 
            onClick={() => onAddToCart(product)}
            className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-bold py-2.5 rounded-xl flex items-center justify-center gap-1.5"
          >
            <ShoppingCart size={16} />
            Ajouter
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

export default function NouveautesPage() {
  const navigate = useNavigate();
  const { addToCart } = useBuyer();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNewProducts();
  }, []);

  const fetchNewProducts = async () => {
    setLoading(true);
    try {
      const now = new Date();
      // Données simulées - Nouveautés
      setProducts([
        {
          id: 'demo-product-new-1',
          title: 'Perforateur SDS+ Bosch GBH 2-28 F - Nouveau modèle 2026',
          price: 289,
          originalPrice: 349,
          brand: 'Bosch Professional',
          image: 'https://images.unsplash.com/photo-1770763233593-74dfd0da7bf0?w=400',
          createdAt: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString(),
          isLimited: true,
          stock: 12,
        },
        {
          id: 'demo-product-new-2',
          title: 'Visseuse à chocs 18V Makita DTD157 - Edition 2026',
          price: 199,
          brand: 'Makita',
          image: 'https://images.pexels.com/photos/13792037/pexels-photo-13792037.jpeg?auto=compress&w=400',
          createdAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
          isLimited: false,
        },
        {
          id: 'demo-product-new-3',
          title: 'Niveau laser croix vert Bosch GCL 2-50 CG',
          price: 349,
          originalPrice: 399,
          brand: 'Bosch Professional',
          image: 'https://images.pexels.com/photos/6474119/pexels-photo-6474119.jpeg?auto=compress&w=400',
          createdAt: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          isLimited: true,
          stock: 8,
        },
        {
          id: 'demo-product-new-4',
          title: 'Scie sauteuse sans fil DeWalt DCS334N - Brushless',
          price: 179,
          brand: 'DeWalt',
          image: 'https://images.pexels.com/photos/30871012/pexels-photo-30871012.jpeg?auto=compress&w=400',
          createdAt: new Date(now.getTime() - 4 * 24 * 60 * 60 * 1000).toISOString(),
          isLimited: false,
        },
        {
          id: 'demo-product-new-5',
          title: 'Coffret douilles 1/2" 56 pièces Facom - Nouvelle gamme',
          price: 449,
          originalPrice: 529,
          brand: 'Facom',
          image: 'https://images.pexels.com/photos/5691656/pexels-photo-5691656.jpeg?auto=compress&w=400',
          createdAt: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString(),
          isLimited: true,
          stock: 5,
        },
        {
          id: 'demo-product-new-6',
          title: 'Tableau électrique connecté Legrand Drivia Smart',
          price: 189,
          brand: 'Legrand',
          image: 'https://images.pexels.com/photos/5767595/pexels-photo-5767595.jpeg?auto=compress&w=400',
          createdAt: new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000).toISOString(),
          isLimited: false,
        },
        {
          id: 'demo-product-new-7',
          title: 'Robinet thermostatique connecté Grohe SmartControl',
          price: 429,
          originalPrice: 499,
          brand: 'Grohe',
          image: 'https://images.unsplash.com/photo-1761353855019-05f2f3ed9c43?w=400',
          createdAt: new Date(now.getTime() - 8 * 24 * 60 * 60 * 1000).toISOString(),
          isLimited: true,
          stock: 15,
        },
        {
          id: 'demo-product-new-8',
          title: 'Peinture écologique Tollens Nature - Nouvelle formule',
          price: 79,
          brand: 'Tollens',
          image: 'https://images.unsplash.com/photo-1621685682093-3b8016dcb57d?w=400',
          createdAt: new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000).toISOString(),
          isLimited: false,
        },
      ]);
    } catch (e) {
      console.error('Error fetching new products:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    addToCart({
      id: product.id,
      name: product.title,
      price: product.price,
      image: product.image,
      quantity: 1,
    });
    toast.success(`${product.title.substring(0, 30)}... ajouté au panier !`);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="nouveautes-page">
      {/* Header Global */}
      <UnifiedHeader />
      
      {/* Navigation Marketplace */}
      <MainNavigation />

      {/* Spacer pour compenser les headers fixed */}
      <div className="h-11 md:hidden" />
      <div className="hidden md:block" style={{ height: '148px' }} />

      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px)'
          }} />
        </div>
        <div className="max-w-7xl mx-auto px-4 py-8 md:py-10 relative">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-3">
              <Sparkles size={32} className="text-yellow-300" />
              <h1 className="text-3xl md:text-4xl font-black text-white">NOUVEAUTÉS</h1>
              <Sparkles size={32} className="text-yellow-300" />
            </div>
            <p className="text-white/90 text-base mb-4">
              Découvre les derniers produits ajoutés sur AbracadaWorld
            </p>
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur rounded-full px-5 py-2.5">
              <Calendar size={18} className="text-yellow-300" />
              <span className="text-white font-medium text-sm">Mis à jour quotidiennement</span>
            </div>
          </div>
        </div>
      </div>

      {/* Badges de confiance */}
      <div className="bg-white border-b border-gray-200 py-3">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-center gap-6 md:gap-8 flex-wrap">
            {[
              { icon: Zap, text: 'Sans intermédiaire' },
              { icon: Tag, text: 'Zéro commission' },
              { icon: Shield, text: 'Paiement sécurisé' },
              { icon: Award, text: 'Vendeur certifié' },
              { icon: Star, text: 'Avis vérifiés' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-gray-600">
                <item.icon size={18} className="text-blue-500" />
                <span className="text-xs md:text-sm font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <main className="flex-1 max-w-7xl mx-auto px-4 py-6 w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900">
              {products.length} nouveaux produits
            </h2>
            <p className="text-gray-500 text-sm">Ajoutés ces 2 dernières semaines</p>
          </div>
          <Link 
            to="/produits"
            className="text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 text-sm"
          >
            Voir tous les produits <ChevronRight size={16} />
          </Link>
        </div>

        {/* Grille des produits - 4 par ligne */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {products.map((product) => (
              <NewProductCard
                key={product.id}
                product={product}
                onAddToCart={handleAddToCart}
              />
            ))}
          </div>
        )}

        {/* CTA Newsletter */}
        <div className="mt-10 bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-6 md:p-8 text-center">
          <Sparkles size={28} className="text-cyan-400 mx-auto mb-3" />
          <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
            Sois le premier informé des nouveautés !
          </h3>
          <p className="text-gray-400 mb-5 text-sm">
            Inscris-toi pour recevoir les derniers produits en avant-première
          </p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input 
              type="email"
              placeholder="Ton email"
              className="flex-1 px-4 py-2.5 rounded-xl border-0 focus:ring-2 focus:ring-blue-500 text-sm"
            />
            <Button className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:opacity-90 text-white font-bold px-5 py-2.5 rounded-xl text-sm">
              M'inscrire
            </Button>
          </div>
        </div>
      </main>

      {/* Footer Global */}
      
    </div>
  );
}
